// =============================================================================
// src/components/worklist/WorklistBits.jsx - shared pieces for every worklist
// (CM Today, Provider / Front Desk panel, CHW My Day + Action Items, and the
// Manager / Billing / MA Today lists).
//
//   <ReasonChips reasons max />        why an item is up next (rank.js)
//   <WhoIsOn claimedName claimedByMe viewers />   claimed + viewing now
//   <WorklistSearch value onChange inputRef />
//   <ShortcutHelp open onClose keys />
//   useListKeys({ enabled, ids, focusedId, setFocusedId, onKey })
//   <DetailPane title subtitle onClose actions inline>   right-side detail
//   <AssignSelect staff itemTypes onAssign />   managers hand an item to a person
// =============================================================================
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { C } from "../../lib/tokens";
import { Badge, Modal } from "../ui";
import { IconSearch, IconX } from "../icons";
import { TONE_VARIANT } from "../../lib/worklist/rank";
import { roleCanAct } from "../../lib/worklist/assign";

export function ReasonChips({ reasons, max = 3, label = true }) {
  const list = (reasons || []).slice(0, max);
  if (!list.length) return null;
  return (
    <span data-testid="reason-chips" style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {label && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: C.textTertiary }}>Why</span>}
      {list.map((r) => <Badge key={r.text} label={r.text} variant={TONE_VARIANT[r.tone] || "neutral"} size="xs" />)}
    </span>
  );
}

export function WhoIsOn({ claimedName, claimedByMe, viewers }) {
  const v = (viewers || []).filter(Boolean);
  if (!claimedName && !claimedByMe && !v.length) return null;
  return (
    <span data-testid="who-is-on" style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {claimedByMe && <Badge label="Claimed by you" variant="teal" size="xs" />}
      {!claimedByMe && claimedName && <Badge label={"Claimed: " + claimedName} variant="teal" size="xs" />}
      {v.length > 0 && (
        <span title={v.join(", ") + " has this open"} style={{
          display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600,
          color: C.textSecondary, padding: "1px 7px", borderRadius: 999, border: "0.5px solid " + C.borderMid,
        }}>
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 999, background: C.greenText }} />
          {v.length === 1 ? v[0] + " is viewing" : v.length + " people viewing"}
        </span>
      )}
    </span>
  );
}

export function WorklistSearch({ value, onChange, inputRef, placeholder = "Search name or item  ( / )", style }) {
  return (
    <div style={{ position: "relative", minWidth: 0, ...(style || {}) }}>
      <span style={{ position: "absolute", left: 9, top: 7, color: C.textTertiary }}><IconSearch size={14} /></span>
      <input ref={inputRef} type="search" aria-label="Search worklist" placeholder={placeholder}
        value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%", boxSizing: "border-box", padding: "6px 10px 6px 30px", fontSize: 12,
          border: "0.5px solid " + C.borderMid, borderRadius: 8, background: C.bgPrimary, color: C.textPrimary, fontFamily: "inherit",
        }} />
    </div>
  );
}

const BASE_KEYS = [
  ["j / k", "Next / previous item"], ["Enter", "Do the main action"], ["o", "Open details"],
  ["c", "Claim"], ["s", "Snooze 1 day"], ["d", "Dismiss"], ["x", "Select for bulk actions"],
  ["/", "Search"], ["Esc", "Close"], ["?", "This help"],
];

export function ShortcutHelp({ open, onClose, keys }) {
  if (!open) return null;
  const list = keys || BASE_KEYS;
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose} maxWidth={420}>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", fontSize: 13 }}>
        {list.map(([k, d]) => (
          <div key={k} style={{ display: "contents" }}>
            <kbd style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 6, border: "0.5px solid " + C.borderMid, background: C.bgSecondary, color: C.textPrimary, textAlign: "center" }}>{k}</kbd>
            <span style={{ color: C.textSecondary }}>{d}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function typing(t) {
  if (!t) return false;
  const tag = (t.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || t.isContentEditable;
}

// Keyboard for a list: j/k move focus; every other key is handed to onKey with
// the focused id. Ignored while typing, with modifier keys, or when a modal
// dialog is open (role="dialog" aria-modal="true" in the document).
export function useListKeys({ enabled = true, ids, focusedId, setFocusedId, onKey }) {
  useEffect(() => {
    if (!enabled) return undefined;
    const h = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== "Escape" && typing(e.target)) return;
      if (e.key !== "Escape" && document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const list = ids || [];
      if (e.key === "j" || e.key === "k") {
        if (!list.length) return;
        e.preventDefault();
        const i = list.indexOf(focusedId);
        const next = e.key === "j" ? Math.min(list.length - 1, i + 1) : Math.max(0, (i < 0 ? 0 : i) - 1);
        setFocusedId(list[next]);
        const el = document.querySelector('[data-wl-id="' + list[next] + '"]');
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
        return;
      }
      if (onKey && onKey(e.key, focusedId, e) === true) e.preventDefault();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [enabled, ids, focusedId, setFocusedId, onKey]);
}

// Right-side detail panel. Inline when `inline` (wide layouts), otherwise a
// fixed drawer over the page.
export function DetailPane({ title, subtitle, onClose, children, actions, inline = false }) {
  const body = (
    <div role={inline ? "region" : "dialog"} aria-label="Item details" data-testid="wl-detail" style={{
      display: "flex", flexDirection: "column", height: "100%", minHeight: 0, background: C.bgPrimary,
    }}>
      <div style={{ padding: "14px 16px 10px", borderBottom: "0.5px solid " + C.borderLight, display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.textPrimary, overflowWrap: "anywhere" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>{subtitle}</div>}
        </div>
        <button type="button" aria-label="Close details" onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", color: C.textSecondary, padding: 4 }}><IconX size={18} /></button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {children}
      </div>
      {actions && <div style={{ padding: "10px 16px", borderTop: "0.5px solid " + C.borderLight, display: "flex", gap: 6, flexWrap: "wrap" }}>{actions}</div>}
    </div>
  );
  if (inline) return body;
  // Portalled to <body>: the page content sits in its own stacking context
  // under the sticky app header, so a drawer rendered in place was covered by
  // the header on phones (close button unreachable).
  const drawer = (
    <div style={{ position: "fixed", inset: 0, zIndex: 2500, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: C.scrim }} />
      <div style={{
        position: "relative", width: "min(440px, 100vw)", height: "100%", boxShadow: C.shadowLg,
        paddingTop: "env(safe-area-inset-top, 0px)", paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxSizing: "border-box", background: C.bgPrimary,
      }}>{body}</div>
    </div>
  );
  return typeof document !== "undefined" ? createPortal(drawer, document.body) : drawer;
}

// Label / value line for the detail pane.
export function Fact({ label, children }) {
  if (children == null || children === "" || children === false) return null;
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textTertiary, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: C.textPrimary, overflowWrap: "anywhere" }}>{children}</div>
    </div>
  );
}

// "Assign to..." picker. `itemTypes` limits the people offered to those whose
// role can work every one of the item types (lib/worklist/assign.js).
export function AssignSelect({ staff, itemTypes, onAssign, disabled, label = "Assign to..." }) {
  const types = (itemTypes || []).filter(Boolean);
  const people = (staff || []).filter((u) => types.every((t) => roleCanAct(u.role, t)));
  return (
    <select aria-label="Assign to" value="" disabled={disabled || people.length === 0}
      onChange={(e) => { if (e.target.value) onAssign(e.target.value); }}
      style={{
        fontSize: 12, padding: "6px 8px", borderRadius: 8, border: "0.5px solid " + C.borderMid,
        background: C.bgPrimary, color: C.textPrimary, fontFamily: "inherit", maxWidth: 200,
      }}>
      <option value="">{people.length ? label : "Nobody can take this"}</option>
      {people.map((u) => <option key={u.id} value={u.id}>{u.full_name + (u.role ? " (" + u.role + ")" : "")}</option>)}
    </select>
  );
}
